import { StandardEvents } from '@shopify/events';
import { deliveryDates, deliveryStatus } from '@aircare/delivery-dates';

class DeliveryEta extends HTMLElement {
  #section;
  #generation = 0;
  #timer;

  connectedCallback() {
    this.#section = this.closest('.shopify-section');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
    document.addEventListener('visibilitychange', this.#renderDates);
    this.#renderDates();
    this.#timer = setInterval(this.#renderDates, 60000);
  }

  disconnectedCallback() {
    this.#generation += 1;
    clearInterval(this.#timer);
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
    document.removeEventListener('visibilitychange', this.#renderDates);
  }

  #renderDates = () => {
    if (this.hasAttribute('aria-busy')) return;
    const { status, date } = deliveryStatus(this.dataset.available === 'true', this.dataset.leadTime || '');
    this.dataset.status = status;
    const label = this.querySelector('[data-stock-label]');
    const text = status === 'backorder'
      ? (this.dataset.backorderTemplate || '').replace('[date]', date)
      : status === 'in-stock' ? this.dataset.inStockLabel : this.dataset.outOfStockLabel;
    if (label && label.textContent !== text) label.textContent = text || '';
    const estimate = this.querySelector('[data-delivery-estimate]');
    if (!estimate) return;
    let message = '';
    if (status === 'out-of-stock') message = this.dataset.contact || '';
    if (status === 'in-stock') {
      let dates;
      try {
        dates = deliveryDates(JSON.parse(this.dataset.days || 'null'));
      } catch {
        dates = null;
      }
      if (dates) message = (this.dataset.dateTemplate || '')
        .replace('[earliest]', dates.earliest).replace('[latest]', dates.latest);
    }
    if (estimate.textContent !== message) estimate.textContent = message;
    estimate.hidden = !message;
  };

  /** @param {import('@shopify/events').ProductSelectEvent} event */
  #onProductSelect = async (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    const generation = ++this.#generation;
    this.setAttribute('aria-busy', 'true');
    const estimate = this.querySelector('[data-delivery-estimate]');
    if (estimate) estimate.hidden = true;
    try {
      const { detail } = await event.promise;
      if (!this.isConnected || generation !== this.#generation) return;
      if (!detail?.html || (!detail.newProduct && detail.productId && String(detail.productId) !== this.dataset.productId)) return;
      const next = detail.html.querySelector('ac-delivery-eta');
      if (!next) return;
      for (const attribute of ['data-product-id', 'data-available', 'data-status', 'data-lead-time', 'data-backorder-template',
        'data-in-stock-label', 'data-out-of-stock-label', 'data-contact', 'data-days', 'data-date-template']) {
        this.setAttribute(attribute, next.getAttribute(attribute) || '');
      }
      this.replaceChildren(...Array.from(next.childNodes, (node) => node.cloneNode(true)));
      this.removeAttribute('aria-busy');
      this.#renderDates();
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[delivery-eta] Variant update failed:', error);
    } finally {
      if (generation === this.#generation) {
        this.removeAttribute('aria-busy');
        this.#renderDates();
      }
    }
  };
}

if (!customElements.get('ac-delivery-eta')) {
  customElements.define('ac-delivery-eta', DeliveryEta);
}
